import React, { FC, useEffect, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { RetailerSearchResult } from '@packages/models/api';
import { StyleSheet } from 'react-native';
import { techlineService } from '@web/services/singletons';
import { Typeahead } from 'react-bootstrap-typeahead';
import { Typography, Icon, Button, TextInputHelper } from '@packages/ui/shared';
import { useAbortController } from '@packages/core/http';
import { getLogger } from '@packages/core/logging';
import colors from '@packages/core/styles/colors';
import ReactDOM from 'react-dom';

const logger = getLogger('techline');

const FILTER_OPTIONS = {
    submission: ['All', 'Today', 'Yesterday', 'This week', 'This month'].map((label) => ({ label })),
    status: [
        { label: 'In Review', value: 'IN_REVIEW' },
        { label: 'Draft', value: 'DRAFT' },
        { label: 'Pending Techline', value: 'PENDING_TECHLINE' },
        { label: 'Update Requested', value: 'UPDATE_REQUESTED' },
        { label: 'Closed', value: 'CLOSED' },
    ],
};

const useStyles = createUseStyles({
    panel: {
        backgroundColor: colors.white,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        maxWidth: 390,
        position: 'absolute',
        right: 0,
        top: 0,
        width: '100%',
        zIndex: 5,
    },
    panelHeader: {
        alignItems: 'center',
        backgroundColor: colors.grayTwo,
        borderBottom: `1px solid ${colors.grayThree}`,
        display: 'flex',
        height: 56,
        justifyContent: 'space-between',
        padding: '0px 24px',
    },
    panelScrollContainer: {
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        overflowY: 'auto',
        padding: 24,
    },
    actionButtons: { marginTop: 24 },
    closeButton: { '&:hover': { cursor: 'pointer' } },
    divider: { height: 1, backgroundColor: colors.grayThree },
    firstSection: { marginBottom: 24 },
    lastDivider: { marginTop: 'auto!important' },
    section: { marginBottom: 24, marginTop: 24 },
});

const styles = StyleSheet.create({
    filterTitle: { fontSize: 11, fontWeight: '700', lineHeight: 16 },
    headerTitle: { fontSize: 15, fontWeight: '400', lineHeight: 24 },
});

interface Filters {
    vin: string;
    selectedStatus: any[];
    selectedWorksheets: any[];
    selectedSubmissionDate: any[];
}

interface Props {
    applyFilters: (filters: any) => void;
    clearFilters: () => void;
    onClose: () => void;
    filters: Filters;
    selectedRetailer?: RetailerSearchResult;
    currentStatusFilter?: string;
}

const Divider: FC<{ className?: string }> = ({ className }) => <hr className={className} />;

export const TechlineQuickFilters: FC<Props> = ({
    onClose,
    filters,
    applyFilters,
    clearFilters,
    currentStatusFilter,
}) => {
    const [vin, setVin] = useState(filters.vin);
    const [selectedStatus, setSelectedStatus] = useState(filters.selectedStatus);
    const isStatusDisabled = !!currentStatusFilter && currentStatusFilter !== 'ALL';
    const lockedStatusOption = isStatusDisabled
        ? FILTER_OPTIONS.status.filter((s) => s.value === currentStatusFilter)
        : null;
    const [selectedWorksheets, setSelectedWorksheets] = useState(filters.selectedWorksheets);
    const [selectedSubmissionDate, setSelectedSubmissionDate] = useState(filters.selectedSubmissionDate);
    const [worksheetOptions, setWorksheetOptions] = useState<{ label: string; value: string }[]>([]);
    const { abortSignalRef } = useAbortController();
    const classes = useStyles();

    useEffect(() => {
        (async () => {
            try {
                const worksheetsRes = await techlineService.fetchPublishedWorksheets();
                if (worksheetsRes?.data && Array.isArray(worksheetsRes.data) && worksheetsRes.data.length) {
                    const worksheetOptionsFormatted = worksheetsRes.data.map((worksheet: string) => ({
                        label: worksheet,
                        value: worksheet,
                    }));
                    setWorksheetOptions(worksheetOptionsFormatted);
                }
            } catch (error) {
                logger.error('Failed to fetch published worksheets', { error });
                setWorksheetOptions([]);
            }
        })();
    }, [abortSignalRef]);

    const rightPanelDiv = document.getElementById('right-side-panel');
    if (!rightPanelDiv) return null;

    const applyAndClose = () => {
        applyFilters({ vin, selectedStatus, selectedWorksheets, selectedSubmissionDate });
        onClose();
    };

    const hasNonStatusChanges =
        vin !== filters.vin ||
        JSON.stringify(selectedWorksheets) !== JSON.stringify(filters.selectedWorksheets) ||
        JSON.stringify(selectedSubmissionDate) !== JSON.stringify(filters.selectedSubmissionDate);

    const isApplyDisabled = isStatusDisabled
        ? !hasNonStatusChanges
        : !vin &&
          selectedStatus?.length === 0 &&
          selectedWorksheets?.length === 0 &&
          selectedSubmissionDate?.length === 0;

    return ReactDOM.createPortal(
        <div className={classes.panel}>
            <div className={classes.panelHeader}>
                <Typography style={styles.headerTitle}>Filters</Typography>
                <div className={classes.closeButton} onClick={onClose}>
                    <Icon name="x-close" />
                </div>
            </div>
            <div className={classes.panelScrollContainer}>
                <div className={classes.firstSection}>
                    <TextInputHelper
                        label="VIN"
                        value={vin}
                        labelStyle={styles.filterTitle}
                        onChangeText={setVin}
                        placeholder="Enter VIN number"
                    />
                </div>
                <Divider className={classes.divider} />
                <div className={classes.section}>
                    <Typography style={styles.filterTitle}>STATUS</Typography>
                    <Typeahead
                        id="typeahead-status-select"
                        multiple
                        className="mt-3"
                        onChange={setSelectedStatus}
                        options={FILTER_OPTIONS.status}
                        placeholder="Select status"
                        selected={lockedStatusOption ?? selectedStatus}
                        disabled={isStatusDisabled}
                    />
                </div>
                <Divider className={classes.divider} />
                <div className={classes.section}>
                    <Typography style={styles.filterTitle}>WORKSHEET</Typography>
                    <Typeahead
                        id="typeahead-worksheet-select"
                        multiple
                        className="mt-3"
                        onChange={setSelectedWorksheets}
                        labelKey="label"
                        options={worksheetOptions}
                        placeholder="Select worksheet"
                        selected={selectedWorksheets}
                    />
                </div>
                <Divider className={classes.divider} />
                <div className={classes.section}>
                    <Typography style={styles.filterTitle}>SUBMISSION DATE</Typography>
                    <Typeahead
                        id="typeahead-submission-date-select"
                        className="mt-3"
                        onChange={setSelectedSubmissionDate}
                        options={FILTER_OPTIONS.submission}
                        placeholder="Select date"
                        selected={selectedSubmissionDate}
                    />
                </div>
                <Divider className={`${classes.divider} ${classes.lastDivider}`} />
                <div className={`d-flex flex-row align-items-center justify-content-between ${classes.actionButtons}`}>
                    <Button onPress={applyAndClose} disabled={isApplyDisabled}>
                        Apply
                    </Button>
                    <Button
                        variant="ghost-blue"
                        disabled={isApplyDisabled}
                        onPress={() => {
                            clearFilters();
                            onClose();
                        }}
                    >
                        Clear all
                    </Button>
                </div>
            </div>
        </div>,
        rightPanelDiv
    );
};
